import { StaticImage } from 'gatsby-plugin-image';
import type { FC } from 'react';
import { SwiperSlide } from 'swiper/react';

import { AddOnsTemplate } from '@/components/add-ons/template';
import { GalleriesItem } from '@/components/galleries/item';
import { GalleriesList } from '@/components/galleries/list';
import { ComponentsHead } from '@/components/head';
import { LinkWithTitle } from '@/components/links';

export const DAVIS_ISLAND_TITLE = 'Davis Island';

export const Head = () => {
  return (
    <ComponentsHead
      title="Private Boat Tours Around Davis Island | Tampa Bay"
      description="Set sail around Davis Island and explore quiet shores, historic homes, and open waters. Our private charters offer the best local views from the water."
      keywords="Davis Island, Historic Homes, Tampa Bay, Island Cruising"
    />
  );
};
const Page: FC = () => {
  return (
    <AddOnsTemplate
      title="Davis Island Boat Charters – Cruise Tampa’s Most Charming Shores"
      subtitle=""
    >
      <GalleriesList>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${DAVIS_ISLAND_TITLE} header`}
              src="../../images/areas/davis-island/0.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${DAVIS_ISLAND_TITLE} slide 1`}
              src="../../images/areas/davis-island/1.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${DAVIS_ISLAND_TITLE} slide 2`}
              src="../../images/areas/davis-island/2.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
      </GalleriesList>
      <p>
        Nestled just off the coast of{' '}
        <LinkWithTitle to="https://www.tampa.gov/guide/downtown-tampa">
          Downtown Tampa
        </LinkWithTitle>
        , Davis Island is one of the most charming and
        scenic destinations we explore at Tampa Bay Boating
        Adventures. Known for its waterfront estates,
        historic yacht club, and quiet inlets, this area
        provides a relaxing backdrop for any private
        charter.
      </p>
      <p>
        Our Davis Island charters offer a little something
        for everyone. Whether you're gliding past
        multi-million dollar mansions, anchoring near the
        sandy shallows to swim, or simply floating along the
        calm water with music and good company, you'll get a
        true taste of coastal living.
      </p>
      <p>
        The calm, protected waters make this area ideal for
        swimming, lounging, or even browsing the bayside
        mansions. It’s a local favorite for good reason—and
        there’s no better way to see it than by boat.
      </p>
      <h3>Available Charters Near Downtown Tampa:</h3>
      <ul>
        <li>
          <LinkWithTitle to="/services/8-hour-private-party-boat-charter/">
            8-Hour Private Party Boat
          </LinkWithTitle>
          : Spend a full day discovering the beauty of Davis
          Island and surrounding bays. Relax, swim, explore,
          and take your time enjoying the sun.
        </li>
        <li>
          <LinkWithTitle to="/services/6-hour-private-party-boat-charter">
            6-Hour Private Party Boat
          </LinkWithTitle>
          : With six hours on the water, there’s plenty of
          time to unwind and see more of what this area has
          to offer—whether it's a scenic cruise or anchoring
          for a long swim.
        </li>
        <li>
          <LinkWithTitle to="/services/4-hour-private-party-boat-charter/">
            4-Hour Private Party Boat
          </LinkWithTitle>
          : Ideal for a half-day escape around Davis Island.
          Enjoy a casual cruise, take in the waterfront
          views, and maybe dip into the bay.
        </li>
        <li>
          <LinkWithTitle to="/services/3-hour-wave-rave-dj-nightime-party-boat-charter">
            3-Hour Sunset Charter
          </LinkWithTitle>
          : End your day with golden skies as we drift past
          Davis Island’s shoreline. Watch the sky turn from
          amber to deep pink as the sun dips below the
          horizon.
        </li>
        <li>
          <LinkWithTitle to="/services/3-hour-wave-rave-dj-nightime-party-boat-charter/">
            3-Hour Wave Rave Charter
          </LinkWithTitle>
          : Quick, lively, and full of good vibes—perfect
          for a short, fun trip through Davis Island’s
          beautiful waterways.
        </li>
        <li>
          <LinkWithTitle to="/services/private-snorkeling-charters-st-petersburg/">
            Snorkeling Charters
          </LinkWithTitle>
          : While we don’t offer snorkeling at Davis Island,
          we can begin the trip here to St Petersburg and
          Egmont Key which are great for spotting fish and
          marine life.
        </li>
        <li>
          <LinkWithTitle to="/services/large-group-charter-boat/">
            Large Group Boat Charter
          </LinkWithTitle>
          : Planning a bigger get-together? Bring your group
          aboard and let us show you the best of Davis
          Island from the water, with room to celebrate,
          socialize, or just soak in the scenery.
        </li>
      </ul>
      <p>
        <LinkWithTitle to="https://www.tampa.gov/neighborhoods/davis-islands">
          Davis Island
        </LinkWithTitle>{' '}
        is truly a hidden gem. With its peaceful shoreline,
        postcard-worthy views, and upscale feel, it’s a
        must-see destination on the water. Whether you’re
        planning a celebration, a sightseeing tour, or a
        relaxing day with friends, our Davis Island charters
        make it easy to create unforgettable memories on the
        bay.
      </p>
    </AddOnsTemplate>
  );
};

export default Page;
